// OBS-02 — client-side error reporter (docs/architecture/16-observability-and-operational-safety.md §5).
// Error boundaries call reportClientError() (doc 13 §4.3: "Errors are logged to the
// server with route, user, branch and correlation id"). The payload is capped to the
// same constants the sink enforces, so a boundary can never be rejected for size.
// Pure builder is exported for unit testing; the fetch is fire-and-forget and never
// throws, so a failed report can never break the error boundary itself.

import {
  CLIENT_ERROR_ID,
  MAX_MESSAGE_LENGTH,
  MAX_ORIGIN_LENGTH,
  MAX_RELEASE_LENGTH,
  MAX_ROUTE_LENGTH,
  MAX_STACK_LENGTH,
} from '@/lib/server/observability/client-error-schema';

export type ClientErrorLevel = 'info' | 'warn' | 'error';

export type ClientErrorReport = {
  /** Route path the error occurred on. Required by the sink. */
  route: string;
  /** Joins the report to the failed request's correlation id when the client has one. */
  correlationId?: string;
  message: string;
  stack?: string;
  level?: ClientErrorLevel;
  /**
   * Host the report came from. Defaults to `window.location.host`, so callers
   * never have to supply it; passed explicitly only by tests.
   *
   * Audit ISSUE-26: reports carried no environment marker, and local dev shares
   * a database with production, so a channel holding 173 reports could not say
   * which were customer-facing. Everything else in the payload (route, message,
   * stack) is identical between a developer's machine and a live host.
   */
  origin?: string;
  /**
   * Build/release id, used to pick the matching source map when a minified stack
   * is symbolicated server-side (OBS-02b). Optional.
   */
  release?: string;
};

export function buildClientErrorPayload(report: ClientErrorReport): Record<string, string> {
  const payload: Record<string, string> = {
    route: report.route.slice(0, MAX_ROUTE_LENGTH),
    message: report.message.slice(0, MAX_MESSAGE_LENGTH),
  };

  if (report.correlationId && CLIENT_ERROR_ID.test(report.correlationId)) {
    payload.correlation_id = report.correlationId;
  }
  if (report.stack) {
    payload.stack = report.stack.slice(0, MAX_STACK_LENGTH);
  }
  if (report.level) {
    payload.level = report.level;
  }

  if (report.release) {
    payload.release = report.release.slice(0, MAX_RELEASE_LENGTH);
  }

  const origin =
    report.origin ?? (typeof window !== 'undefined' ? window.location.host : undefined);
  if (origin) {
    payload.origin = origin.slice(0, MAX_ORIGIN_LENGTH);
  }

  return payload;
}

export async function reportClientError(report: ClientErrorReport): Promise<void> {
  try {
    const payload = buildClientErrorPayload(report);
    await fetch('/api/observability/client-error', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Fire-and-forget: reporting must never surface inside the error boundary.
  }
}
